import { createBox3D, type Box3D } from 'box3d-web';
import {
  COTTAGE_FIXED_DT,
  type CottageEvent,
  type CottagePoint,
  type CottageSnapshot,
  type CottageWeapon,
} from '../../structures/cottage/cottagePhysics';
import { COTTAGE_PIECES } from '../../structures/cottage/cottageSpec';
import { createDestructionIdentityFactory } from '../identities';
import {
  CottageStructureAdapter,
  createCottageStructureAdapter,
  type CottageCanonicalPresentationEvent,
} from './cottageStructureAdapter';
import {
  DestructionSceneRuntime,
  type DestructionSceneEventEnvelope,
  type DestructionSceneTelemetry,
} from '../destructionSceneRuntime';
import { SharedDestructionWorld } from '../sharedDestructionWorld';

export interface CottageTownStructureSnapshot {
  readonly structureKey: string;
  readonly ordinal: number;
  readonly worldOffset: CottagePoint;
  readonly cottage: CottageSnapshot;
  readonly revision: number;
}

export interface CottageTownSnapshot {
  readonly structureCount: number;
  readonly authoredNodeCount: number;
  readonly physicalNodeCount: number;
  readonly logicalNodeCount: number;
  readonly nativeBodyCount: number;
  readonly nativeShapeCount: number;
  readonly registry: ReturnType<SharedDestructionWorld['snapshot']>['registry']['stats'];
  readonly scene: DestructionSceneTelemetry;
  readonly structures: readonly CottageTownStructureSnapshot[];
}

export type CottageTownCanonicalEvent = CottageCanonicalPresentationEvent & { readonly structureKey: string };

export const COTTAGE_TOWN_MIN_STRUCTURES = 1;
export const COTTAGE_TOWN_MAX_STRUCTURES = 3;
export const COTTAGE_TOWN_DEFAULT_STRUCTURES = 2;
export const COTTAGE_TOWN_SPACING = 14;

const PAGE_NAMESPACE = 'cottage-town-page';

export function checkedCottageTownStructureCount(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < COTTAGE_TOWN_MIN_STRUCTURES || value > COTTAGE_TOWN_MAX_STRUCTURES) {
    throw new RangeError('cottage town structure count must be an integer from 1 to 3');
  }
  return value;
}

/** Side-by-side X offsets centered on the origin. Count 1 is 0; count 2 is the original ±7 pair. */
export function cottageTownWorldOffsets(structureCount: number): readonly CottagePoint[] {
  const count = checkedCottageTownStructureCount(structureCount);
  const center = (count - 1) / 2;
  return Object.freeze(
    Array.from({ length: count }, (_, ordinal) =>
      Object.freeze({ x: (ordinal - center) * COTTAGE_TOWN_SPACING, y: 0, z: 0 }),
    ),
  );
}

export class CottageTownPageSession {
  readonly box3d: Box3D;
  readonly shared: SharedDestructionWorld;
  readonly scene: DestructionSceneRuntime<CottageEvent>;
  readonly adapters: readonly CottageStructureAdapter[];
  readonly requestedStructureCount: number;
  private disposed = false;
  private readonly pendingResets = new Set<string>();
  private readonly pendingDisposals = new Set<string>();

  private constructor(
    box3d: Box3D,
    shared: SharedDestructionWorld,
    scene: DestructionSceneRuntime<CottageEvent>,
    adapters: readonly CottageStructureAdapter[],
    requestedStructureCount: number,
  ) {
    this.box3d = box3d;
    this.shared = shared;
    this.scene = scene;
    this.adapters = adapters;
    this.requestedStructureCount = requestedStructureCount;
  }

  static async create(options: { structureCount?: number } = {}): Promise<CottageTownPageSession> {
    const structureCount = checkedCottageTownStructureCount(options.structureCount ?? COTTAGE_TOWN_DEFAULT_STRUCTURES);
    const offsets = cottageTownWorldOffsets(structureCount);
    const box3d = await createBox3D();
    const ids = createDestructionIdentityFactory({ namespace: PAGE_NAMESPACE, generation: 0 });
    const worldIdentity = ids.world(0);
    const shared = new SharedDestructionWorld(box3d, worldIdentity, { gravity: [0, -9.8, 0] });
    let scene: DestructionSceneRuntime<CottageEvent> | undefined;
    const adapters: CottageStructureAdapter[] = [];
    try {
      const ground = shared.ensureGround();
      scene = new DestructionSceneRuntime<CottageEvent>(shared, { fixedDt: COTTAGE_FIXED_DT, substeps: 4 });
      for (let ordinal = 0; ordinal < offsets.length; ordinal += 1) {
        const adapter = await createCottageStructureAdapter({
          shared,
          ids,
          worldIdentity,
          ordinal,
          groundBody: ground.body,
          worldOffset: offsets[ordinal],
        });
        adapters.push(adapter);
        scene.attach(adapter);
      }
      return new CottageTownPageSession(box3d, shared, scene, adapters, structureCount);
    } catch (error) {
      try { scene?.dispose(); } catch { /* preserve construction error */ }
      for (const adapter of adapters) {
        try { adapter.dispose(); } catch { /* preserve construction error */ }
      }
      try { shared.dispose(); } catch { /* preserve construction error */ }
      try { box3d.dispose(); } catch { /* preserve construction error */ }
      throw error;
    }
  }

  get isDisposed(): boolean { return this.disposed; }
  get structureKeys(): readonly string[] { return this.adapters.map((adapter) => adapter.structureKey); }
  /** First live adapter — count=1 inspection API (impactAt / reset / runtime). */
  get adapter(): CottageStructureAdapter { return this.primaryAdapter(); }
  get structureKey(): string { return this.primaryAdapter().structureKey; }
  get runtime() { return this.primaryAdapter().cottageRuntime; }

  advance(delta: number): number {
    this.assertLive();
    const stepped = this.scene.advance(delta);
    if (stepped > 0) {
      this.pendingResets.clear();
      for (const structureKey of this.pendingDisposals) {
        const adapter = this.adapters.find((candidate) => candidate.structureKey === structureKey);
        if (adapter?.isDisposed) this.pendingDisposals.delete(structureKey);
      }
    }
    return stepped;
  }

  drainEvents(): readonly DestructionSceneEventEnvelope<CottageEvent>[] {
    this.assertLive();
    return this.scene.drainEvents();
  }

  drainCanonicalEvents(): readonly CottageTownCanonicalEvent[] {
    this.assertLive();
    const events: CottageTownCanonicalEvent[] = [];
    for (const adapter of this.adapters) {
      if (adapter.isDisposed) continue;
      for (const event of adapter.drainCanonicalEvents(this.scene)) {
        events.push(Object.freeze({ ...event, structureKey: adapter.structureKey }));
      }
    }
    return Object.freeze(events);
  }

  impactAt(point: CottagePoint, weapon: CottageWeapon, power: number, direction: CottagePoint = { x: 0, y: 1, z: 0 }): void {
    this.assertLive();
    const structureKey = this.primaryAdapter().structureKey;
    if (this.pendingDisposals.has(structureKey)) return;
    this.scene.enqueueCommand({
      targetStructureKey: structureKey,
      type: 'fire',
      payload: { tool: weapon, power, target: point, direction },
    });
  }

  impactPart(id: string, weapon: CottageWeapon, power: number): void {
    this.assertLive();
    const piece = this.runtime.pieces.find((entry) => entry.spec.id === id);
    if (!piece) return;
    const [x, y, z] = piece.spec.position;
    this.impactAt({ x, y, z }, weapon, power);
  }

  setTool(tool: CottageWeapon): void {
    this.assertLive();
    for (const adapter of this.liveAdapters()) {
      this.scene.enqueueCommand({ targetStructureKey: adapter.structureKey, type: 'set-tool', payload: { tool } });
    }
  }

  setPower(power: number): void {
    this.assertLive();
    for (const adapter of this.liveAdapters()) {
      this.scene.enqueueCommand({ targetStructureKey: adapter.structureKey, type: 'set-weapon-power', payload: { power } });
    }
  }

  reset(): void {
    this.resetStructure(this.primaryAdapter().structureKey);
  }

  strikePart(structureKey: string, pieceId: string, weapon: CottageWeapon = 'cannon', power = 1): void {
    this.assertLive();
    if (this.pendingDisposals.has(structureKey)) return;
    const adapter = this.adapterFor(structureKey);
    const piece = adapter.cottageRuntime.pieces.find((entry) => entry.spec.id === pieceId);
    if (!piece) return;
    const [x, y, z] = piece.spec.position;
    this.scene.enqueueCommand({
      targetStructureKey: structureKey,
      type: 'fire',
      payload: { tool: weapon, power, target: { x, y, z }, direction: { x: 0, y: 1, z: 0 } },
    });
  }

  resetStructure(structureKey: string): void {
    this.assertLive();
    if (this.pendingResets.has(structureKey) || this.pendingDisposals.has(structureKey)) return;
    this.adapterFor(structureKey);
    this.scene.enqueueCommand({ targetStructureKey: structureKey, type: 'reset-structure', payload: {} });
    this.pendingResets.add(structureKey);
  }

  disposeStructure(structureKey: string): void {
    this.assertLive();
    if (this.pendingDisposals.has(structureKey)) return;
    this.adapterFor(structureKey);
    this.scene.enqueueCommand({ targetStructureKey: structureKey, type: 'dispose-structure', payload: {} });
    this.pendingDisposals.add(structureKey);
  }

  snapshot(): CottageTownSnapshot {
    this.assertLive();
    const structures = this.adapters
      .map((adapter, ordinal) => {
        if (adapter.isDisposed) return null;
        const snapshot = adapter.snapshot();
        return Object.freeze({
          structureKey: adapter.structureKey,
          ordinal,
          worldOffset: adapter.worldOffset,
          cottage: snapshot.cottage,
          revision: adapter.revision,
        });
      })
      .filter((structure): structure is NonNullable<typeof structure> => structure !== null);
    const shared = this.shared.snapshot();
    const physicalNodeCount = structures.reduce((sum, structure) => sum + structure.cottage.physicalNodeCount, 0);
    const authoredNodeCount = structures.length * COTTAGE_PIECES.length;
    return Object.freeze({
      structureCount: structures.length,
      authoredNodeCount,
      physicalNodeCount,
      logicalNodeCount: authoredNodeCount - physicalNodeCount,
      nativeBodyCount: shared.nativeBodyCount,
      nativeShapeCount: shared.registry.stats.shapeCount,
      registry: shared.registry.stats,
      scene: this.scene.telemetry(),
      structures: Object.freeze(structures),
    });
  }

  dispose(): void {
    if (this.disposed) return;
    let firstError: unknown;
    const attempt = (operation: () => void) => {
      try { operation(); } catch (error) { if (firstError === undefined) firstError = error; }
    };
    attempt(() => this.scene.dispose());
    for (const adapter of this.adapters) attempt(() => adapter.dispose());
    attempt(() => this.shared.dispose());
    attempt(() => this.box3d.dispose());
    this.pendingResets.clear();
    this.pendingDisposals.clear();
    this.disposed = true;
    if (retained === this) retained = null;
    if (firstError !== undefined) throw firstError;
  }

  private liveAdapters(): CottageStructureAdapter[] {
    return this.adapters.filter((adapter) => !adapter.isDisposed && !this.pendingDisposals.has(adapter.structureKey));
  }

  private primaryAdapter(): CottageStructureAdapter {
    const adapter = this.liveAdapters()[0] ?? this.adapters.find((candidate) => !candidate.isDisposed);
    if (!adapter) throw new Error('unknown Cottage town structure (none live)');
    return adapter;
  }

  private adapterFor(structureKey: string): CottageStructureAdapter {
    const adapter = this.adapters.find((candidate) => candidate.structureKey === structureKey && !candidate.isDisposed);
    if (!adapter) throw new Error(`unknown Cottage town structure ${structureKey}`);
    return adapter;
  }

  private assertLive(): void {
    if (this.disposed) throw new Error('Cottage town page session used after dispose()');
  }
}

let retained: CottageTownPageSession | null = null;
let pending: Promise<CottageTownPageSession> | null = null;

export function getExistingCottageTownPageSession(): CottageTownPageSession | null { return retained; }

export async function getCottageTownPageSession(structureCount = COTTAGE_TOWN_DEFAULT_STRUCTURES): Promise<CottageTownPageSession> {
  const count = checkedCottageTownStructureCount(structureCount);
  if (retained && !retained.isDisposed && retained.requestedStructureCount === count) return retained;
  if (pending) {
    return pending.then(async (session) => {
      if (!session.isDisposed && session.requestedStructureCount === count) return session;
      if (!session.isDisposed && session.requestedStructureCount !== count) session.dispose();
      return getCottageTownPageSession(count);
    });
  }
  if (retained && !retained.isDisposed) {
    if (retained.requestedStructureCount === count) return retained;
    retained.dispose();
    retained = null;
  }
  pending = CottageTownPageSession.create({ structureCount: count })
    .then((session) => { retained = session; pending = null; return session; })
    .catch((error) => { pending = null; throw error; });
  return pending;
}

export function disposeCottageTownPageSession(): void {
  const session = retained;
  retained = null;
  if (session) session.dispose();
}
